import { useEffect, useState } from "react";

export default function PillarsComponent() {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById("pillars");
      if (!el) return;

      const rect = el.getBoundingClientRect();

      if (rect.top < window.innerHeight - 120) {
        setVisible(true);
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const pillars = [
    {
      title: "Climate-Smart Agriculture",
      subtitle: "Practical farming solutions for resilience",
      desc: "We help farming communities adopt climate-smart practices that improve productivity, protect natural resources, and strengthen food security for long-term sustainability.",
      img: "https://www.scidev.net/sub-saharan-africa/wp-content/uploads/sites/5/2019/08/empowering_small_farmers.jpg",
    },
    {
      title: "Women & Youth Empowerment",
      subtitle: "Creating opportunities through inclusion",
      desc: "We empower women and young people through skills development, sustainable livelihoods, entrepreneurship support, and active participation in community climate solutions.",
      img: "https://cenfri.org/wp-content/uploads/East-Africa-cenfri-e1663761698851.jpg",
    },
    {
      title: "Environmental Sustainability",
      subtitle: "Protecting ecosystems for future generations",
      desc: "Our environmental initiatives focus on conservation, environmental education, waste reduction, and community-driven actions that restore and preserve ecosystems.",
      img: "https://www.environewsnigeria.com/wp-content/uploads/2024/08/Women-in-tanzania-planting-trees.jpg",
    },
  ];

  return (
    <section
      id="pillars"
      className="relative bg-[#F7F4EE] py-16 lg:py-28 px-6 md:px-12 lg:px-20 overflow-hidden"
    >
      {/* background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-180px] left-[-120px] w-[500px] h-[500px] bg-green-200/30 blur-3xl rounded-full" />
        <div className="absolute bottom-[-200px] right-[-100px] w-[500px] h-[500px] bg-green-100/30 blur-3xl rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* HEADER */}
        <div
          className={`max-w-5xl transition-all duration-700 ${
            visible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <p className="text-xs tracking-[0.4em] text-green-500 mb-5">
            OUR APPROACH
          </p>

          <p className="mt-8 md:text-lg text-[#5C564C] leading-relaxed max-w-4xl">
            We implement practical, community-based solutions that integrate
            climate-smart agriculture, environmental education, and livelihood
            development.
          </p>

          <p className="mt-4 md:text-lg text-[#5C564C] leading-relaxed max-w-4xl">
            Our approach focuses on action, not theory - ensuring every
            intervention produces measurable results on the ground.
          </p>
        </div>

        {/* MAIN SECTION */}
        <div className="mt-16 flex flex-col-reverse lg:grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT SIDE */}
          <div className="lg:col-span-4 space-y-5">
            {pillars.map((pillar, i) => {
              const isActive = active === i;

              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-full text-left rounded-[28px] border transition-all duration-500 p-6 group ${
                    isActive
                      ? "bg-[#0F172A] border-[#0F172A] shadow-2xl"
                      : "bg-white border-green-100 hover:border-green-400"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p
                        className={`text-xs tracking-[0.25em] mb-3 ${
                          isActive
                            ? "text-green-400"
                            : "text-green-500"
                        }`}
                      >
                        0{i + 1}
                      </p>

                      <h3
                        className={`text-lg lg:text-xl font-semibold transition-all duration-300 ${
                          isActive
                            ? "text-white"
                            : "text-[#1E1E1E]"
                        }`}
                      >
                        {pillar.title}
                      </h3>
                    </div>

                    <div
                      className={`w-3 h-3 rounded-full mt-2 transition-all duration-300 ${
                        isActive
                          ? "bg-green-400 scale-125"
                          : "bg-green-200 group-hover:bg-green-400"
                      }`}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* RIGHT SIDE IMAGE */}
          <div className="lg:col-span-8">
            <div className="relative overflow-hidden rounded-[36px] shadow-2xl min-h-[520px]">

              <img
                src={pillars[active].img}
                alt={pillars[active].title}
                className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
              />

              {/* overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-black/20 to-transparent" />

              {/* floating content */}
              <div className="relative z-10 flex flex-col justify-end h-[520px] p-8 md:p-12 lg:p-16">
                <div className="max-w-2xl">

                  <p className="text-green-400 text-xs tracking-[0.3em] mb-4">
                    KEY FOCUS AREA
                  </p>

                  <h3 className="text-xl lg:text-4xl md:text-3xl font-semibold text-white leading-tight">
                    {pillars[active].title}
                  </h3>

                  <div className="mt-8 flex items-center gap-3">
                    {pillars.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActive(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          active === i
                            ? "w-12 bg-green-400"
                            : "w-4 bg-white/40"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* subtle ring */}
              <div className="absolute inset-0 rounded-[36px] ring-1 ring-green-400/20"></div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}